import { useState } from "react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogFooter, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { resolveImageUrl } from "@/lib/api";
import { X } from "lucide-react";
import { AuthImg, useAuthedImage } from "./AuthImg";
import { calculateMaterials } from "@/lib/calcEngine";
import { CalculationResults } from "./CalculationResults";
import { ZoomableImage } from "./ZoomableImage";

// Pallet type details (like calculator View Details) + the exact Calculation Results below
export function OrderPalletDetailsDialog({ pallet, qty, onClose, onAddToCutList, hideAddToList }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const pImages = pallet ? (pallet.images || (pallet.image ? [pallet.image] : [])) : [];
  const lightboxSrc = useAuthedImage(lightboxIdx !== null ? pImages[lightboxIdx] : null);
  if (!pallet) return null;
  const quantity = parseInt(qty) || 0;
  const result = calculateMaterials(pallet, quantity);
  const images = pImages;
  return (
    <>
    <AlertDialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none max-w-lg w-[calc(100vw-2rem)] mx-auto max-h-[80vh] overflow-y-auto overflow-x-hidden" data-testid="order-pallet-details-dialog">
        <button onClick={onClose} className="sticky top-0 float-right z-10 p-2 -mt-2 -mr-2 ml-auto bg-white/90 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors" style={{ display: 'block' }} data-testid="order-pallet-details-x">
          <X className="w-5 h-5" />
        </button>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-zinc-900 [overflow-wrap:anywhere]" style={{ fontFamily: "Chivo, sans-serif" }}>
            {pallet.name}
          </AlertDialogTitle>
        </AlertDialogHeader>
        <div className="space-y-4 text-sm">
          {pallet.description && <p className="text-zinc-500">{pallet.description}</p>}
          {images.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-2">Photos</p>
              <div className="grid grid-cols-3 gap-2">
                {images.map((img, i) => (
                  <AuthImg key={i} src={img} alt={`${pallet.name} ${i + 1}`}
                    className="w-full h-20 object-cover border border-zinc-200 cursor-pointer hover:border-orange-400 transition-colors"
                    onClick={() => setLightboxIdx(i)} />
                ))}
              </div>
            </div>
          )}
          {pallet.notes && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-1">Notes</p>
              <p className="text-zinc-600 whitespace-pre-wrap">{pallet.notes}</p>
            </div>
          )}
          {(pallet.components || []).map((comp, ci) => (
            <div key={ci} className="border border-zinc-200 p-3" data-testid={`order-pallet-details-comp-${ci}`}>
              <p className="font-bold text-zinc-800 mb-2 [overflow-wrap:anywhere]">{comp.name}</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <span className="text-zinc-400">Qty per pallet:</span><span className="text-zinc-700 font-semibold">{comp.quantity}</span>
                {comp.length && <><span className="text-zinc-400">Length:</span><span className="text-zinc-700">{comp.length}&quot;</span></>}
                {comp.height && <><span className="text-zinc-400">Width:</span><span className="text-zinc-700">{comp.height}&quot;</span></>}
                {comp.thickness && <><span className="text-zinc-400">Thick:</span><span className="text-zinc-700">{comp.thickness}&quot;</span></>}
                {comp.pcs_per_min && <><span className="text-zinc-400">Pcs/min:</span><span className="text-zinc-700">{comp.pcs_per_min}</span></>}
                {comp.board_footage && <><span className="text-zinc-400">Bd ft/pc:</span><span className="text-zinc-700">{comp.board_footage}</span></>}
                {comp.notched != null && <><span className="text-zinc-400">Notched:</span><span className="text-zinc-700">{comp.notched ? `Yes${comp.notch_length ? ` (${comp.notch_length}")` : ''}` : 'No'}</span></>}
                {comp.skids && <><span className="text-zinc-400">Pcs/skid:</span><span className="text-zinc-700">{comp.skids}</span></>}
              </div>
              {comp.lumber?.length > 0 && comp.lumber.some(l => l?.length || l?.quantity) && (
                <div className="mt-2 pt-2 border-t border-zinc-100">
                  <p className="text-[10px] uppercase tracking-wider text-violet-500 font-semibold mb-1">Lumber</p>
                  {comp.lumber.filter(l => l?.length || l?.quantity).map((l, li) => (
                    <p key={li} className="text-xs text-violet-700 [overflow-wrap:anywhere]">
                      {l.quantity} pcs/cant — {l.thickness ? `${l.thickness}"` : ''}{l.width && l.width !== 'Random' ? ` x ${l.width}"` : l.width === 'Random' ? ' x Rndm' : ''} x {l.length}&apos;{l.description ? ` ${l.description}` : ''}
                    </p>
                  ))}
                </div>
              )}
              {comp.extras && (
                <div className="mt-2 pt-2 border-t border-zinc-100">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-1">Extras</p>
                  <p className="text-xs text-zinc-600 whitespace-pre-wrap">{comp.extras}</p>
                </div>
              )}
            </div>
          ))}
          {/* Calculation Results — exactly as on the Calculator page */}
          <CalculationResults
            calculationResult={result}
            addToList={() => onAddToCutList && onAddToCutList(result, null)}
            addComponentToList={(idx) => onAddToCutList && onAddToCutList(result, idx)}
            hideAddToList={hideAddToList || !onAddToCutList}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300" data-testid="order-pallet-details-close">Close</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    {lightboxIdx !== null && images.length > 0 && (
      <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4" style={{ pointerEvents: 'auto' }} onClick={() => setLightboxIdx(null)}>
        <div className="relative max-w-full max-h-[85vh] flex flex-col items-center" onClick={e => e.stopPropagation()}>
          <ZoomableImage src={lightboxSrc} alt="" className="max-w-full max-h-[75vh] object-contain" />
          <button onClick={() => setLightboxIdx(null)} className="absolute top-2 right-2 p-2 bg-black/50 text-white hover:bg-black/70" data-testid="order-details-lightbox-close"><span className="text-lg font-bold">&times;</span></button>
        </div>
      </div>
    )}
    </>
  );
}
